import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, CalendarCheck } from "lucide-react";
import { BOOKING_SERVICES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ = "America/Denver";

const dayFmt = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
const labelFmt = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
const slotFmt = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: "UTC" });
const longDateFmt = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });

function upcomingBusinessDays(count) {
  const [y, m, d] = dayFmt.format(new Date()).split("-").map(Number);
  const cursor = new Date(Date.UTC(y, m - 1, d, 12));
  const out = [];
  while (out.length < count) {
    const dow = cursor.getUTCDay();
    if (dow !== 0 && dow !== 6) out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

const slotLabel = (t) => slotFmt.format(new Date(`1970-01-01T${t}:00Z`)).replace(":00", " ");

export default function BookingCalendar({ preselectService = "", dataTestId = "booking-calendar" }) {
  const days = useMemo(() => upcomingBusinessDays(12), []);
  const [date, setDate] = useState(days[0]);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: BOOKING_SERVICES.some((s) => s.name === preselectService) ? preselectService : "General consultation", notes: "", website: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetch(`${API}/appointments/availability?date=${date}`)
      .then((r) => r.json())
      .then((d) => {
        if (!live) return;
        const next = d.slots || [];
        setSlots(next);
        setTime((t) => (next.some((s) => s.time === t && s.available) ? t : ""));
      })
      .catch(() => live && setSlots([]))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!time) {
      setError("Please pick a time first.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const r = await fetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, date, time }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const raw = (d.errors && Object.values(d.errors)[0]) || (typeof d.detail === "string" ? d.detail : d.detail?.message) || "Something went wrong. Please call us instead.";
        throw new Error(String(raw).replace(/^Value error,\s*/i, ""));
      }
      setBooked({ date, time: slotLabel(time), service: form.service });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (booked) {
    return (
      <div data-testid={`${dataTestId}-success`} className="rounded-md border border-olive/25 bg-white p-6 sm:p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sage text-olive"><CalendarCheck className="h-6 w-6" /></span>
        <h3 className="mt-5 font-serif text-2xl text-olive sm:text-3xl">You’re booked.</h3>
        <p className="mt-3 text-base leading-relaxed text-moss">{longDateFmt.format(new Date(`${booked.date}T12:00:00Z`))} at {booked.time} (Mountain Time) — {booked.service}. The office has been notified, and we’ll see you then.</p>
        <p className="mt-3 text-sm text-moss">Need to move it? Call <a href="tel:+18014745890" data-testid={`${dataTestId}-success-phone`} className="font-bold text-pine underline decoration-olive/40 underline-offset-4">(801) 474-5890</a>.</p>
        <button type="button" onClick={() => { setBooked(null); setTime(""); }} data-testid={`${dataTestId}-book-again`} className="btn-outline mt-6">
          Book another time
        </button>
      </div>
    );
  }

  return (
    <div data-testid={dataTestId} className="rounded-md border border-olive/25 bg-white p-6 sm:p-8">
      <p className="eyebrow">1 · Pick a day</p>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2" data-testid={`${dataTestId}-dates`}>
        {days.map((d) => (
          <button key={d} type="button" onClick={() => setDate(d)} data-testid={`booking-date-${d.replaceAll("-", "")}`} aria-pressed={date === d} className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${date === d ? "border-olive bg-olive text-white" : "border-olive/30 text-olive hover:bg-sage"}`}>
            {labelFmt.format(new Date(`${d}T12:00:00Z`))}
          </button>
        ))}
      </div>

      <p className="eyebrow mt-6">2 · Pick a time <span className="font-sans text-xs font-normal normal-case tracking-normal text-moss/70">(Mountain Time · Mon–Fri, 9–5)</span></p>
      {loading ? (
        <p className="mt-4 text-sm text-moss" data-testid={`${dataTestId}-loading`}>Checking open times…</p>
      ) : (
        <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-8" data-testid={`${dataTestId}-slots`}>
          {slots.map((s) => (
            <button key={s.time} type="button" disabled={!s.available} onClick={() => setTime(s.time)} data-testid={`booking-slot-${s.time.replace(":", "")}`} aria-pressed={time === s.time} className={`rounded-md border px-2 py-2 text-sm font-semibold transition-colors duration-200 ${!s.available ? "cursor-not-allowed border-line bg-sage/40 text-moss/40 line-through" : time === s.time ? "border-olive bg-olive text-white" : "border-olive/30 text-olive hover:bg-sage"}`}>
              {slotLabel(s.time)}
            </button>
          ))}
        </div>
      )}

      <p className="eyebrow mt-6">3 · Tell us who’s coming</p>
      <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2" data-testid={`${dataTestId}-form`}>
        <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid={`${dataTestId}-name`} className="rounded-md border border-olive/30 bg-white px-4 py-3 text-base text-ink focus:border-olive focus:outline-none" />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid={`${dataTestId}-email`} className="rounded-md border border-olive/30 bg-white px-4 py-3 text-base text-ink focus:border-olive focus:outline-none" />
        <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid={`${dataTestId}-phone`} className="rounded-md border border-olive/30 bg-white px-4 py-3 text-base text-ink focus:border-olive focus:outline-none" />
        <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid={`${dataTestId}-service`} className="rounded-md border border-olive/30 bg-white px-4 py-3 text-base text-ink focus:border-olive focus:outline-none">
          <option>General consultation</option>
          {BOOKING_SERVICES.map((s) => <option key={s.name}>{s.name}</option>)}
        </select>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything we should know ahead of time? (optional)" rows="3" data-testid={`${dataTestId}-notes`} className="rounded-md border border-olive/30 bg-white px-4 py-3 text-base text-ink focus:border-olive focus:outline-none sm:col-span-2" />
        <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex="-1" autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] top-[-9999px]" />
        {error && <p className="text-sm text-red-700 sm:col-span-2" data-testid={`${dataTestId}-error`}>{error}</p>}
        <button type="submit" disabled={submitting || !time} data-testid={`${dataTestId}-submit-button`} className="btn-primary disabled:opacity-50 sm:col-span-2">
          {submitting ? "Booking…" : time ? `Confirm ${slotLabel(time)} on ${labelFmt.format(new Date(`${date}T12:00:00Z`))}` : "Confirm appointment"} <ArrowUpRight className="h-4 w-4" />
        </button>
      </form>
      <p className="mt-4 text-xs leading-relaxed text-moss/80">Appointments run Monday to Friday, 9 AM to 5 PM Mountain Time. Booked times disappear from the list, so you can’t double-book us by accident.</p>
    </div>
  );
}